// 3rd-party modules
import {
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { isDesktop } from 'react-device-detect';
import { Avatar, Layout, Menu, theme } from 'antd';
import { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// project modules
import BranchPickerModal from '../components/branch-picker/BranchPickerModal';
import PortalMobileFooterMenu from '../components/global/portalMobileFooterMenu';
import PortalMobileHeaderMenu from '../components/global/portalMobileHeaderMenu';
import useTranslation from '../hooks/TranslationContext';
import { apiCall } from '../helpers/apiHelper';
import { getAvatarColor, getInitials } from '../helpers/objectHelper';
import { getDayName, getFormatDateWithDay } from '../helpers/dateHelper';
import { logoutUser } from '../stores/userStore';
import { RootState } from '../stores';
import { useAppDispatch } from '../hooks/storeHooks';

// apis
import * as AuthApi from '../apis/authApi';

// models
import { MenuItems, MenuItemType } from '../Menu';
import { DestinationResolverModel } from '../models/destinationResolver';
import { User } from '../models/auth';

// defines
type Props = {
  children?: ReactNode
}

const { Content, Sider } = Layout;

export default function DefaultLayout({ children }: Props) {
  const user = useSelector((state: RootState) => state.user.currentUser) as User;
  const destinationResolver = useSelector((state: RootState) => state.user.destinationResolver) || new DestinationResolverModel();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [collapsed, setCollapsed] = useState(false);
  const [currentLink, setCurrentLink] = useState('/');
  const [currentSelectedItem, setCurrentSelectedItem] = useState<string[]>([]);
  const [showBranchPickerModal, setShowBranchPickerModal] = useState(false);

  // Use global translation hook
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { t, changeLanguage, language, isRTL } = useTranslation();
  const abortController = new AbortController();

  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const getCheckAuth = async (abortSignal?: AbortSignal) => {
    const response = await apiCall(AuthApi.checkAuthStatus(abortSignal));

    return response.success;
  };

  const getCheckAuthAsync = async () => {
    await getCheckAuth(abortController.signal);
  }

  const onMenuItemSelect = ({item}: any) => {
    navigate(item.props.link);
  }

  const onLogoutClick = () => {
    dispatch(logoutUser());
    navigate('/auth/login');
  };

  useEffect(() => {
    const current = `/${location.pathname.substring(1)}`;
    setCurrentLink(current);
    getDefaultSelectedMenuItem(current);

    getCheckAuthAsync();
  }, []);

  useEffect(() => {
    const current = `/${location.pathname.substring(1)}`;
    setCurrentLink(current)
  }, [currentLink]);

  const getDefaultSelectedMenuItem = (current: string) => {
    const selectedItems = MenuItems.filter(x => x.link === current);
    if (selectedItems.length) {
      setCurrentSelectedItem([selectedItems[0].key])
    }
    else{
      setCurrentSelectedItem(["home"])
    }
  }

  const onSelectBranchModalSave = (user?: User) => {
    window.location.reload();
  }

  const ProfileMenuItems: MenuItemType[] = [
    {
      key: "switch-lang",
      dir:`${isRTL ? "rtl" : "ltr"}`,
      className: `mt-2`,
      onClick: () => changeLanguage(language === "en" ? "fa" : "en"),
      label: collapsed ?
        language === "en" ? "IR" : "EN" :
        language === "en" ? "🇮🇷 فارسی" : "🇺🇸 English"
    },
    // {
    //   key: "destinationResolver",
    //   dir:`${isRTL ? "rtl" : "ltr"}`,
    //   onClick: () => setShowBranchPickerModal(true),
    //   label: destinationResolver.displayName
    // },
    {
      key: "sign-out",
      dir:`${isRTL ? "rtl" : "ltr"}`,
      onClick: onLogoutClick,
      icon: <icon className="icon-lr">logout</icon>,
      label: t("sign-out")
    }
  ];


  return (
    <div className="flex flex-col min-h-screen max-h-screen">
      {/* Optional Header */}
      <PortalMobileHeaderMenu className="flex-none h-20 md:hidden" onProfileClick={() => setShowBranchPickerModal(true)} onLogoutClick={onLogoutClick} />

      {/* Main Content */}
      <main className="flex-grow overflow-y-auto">
        {
          isDesktop ?
          <Layout className='min-h-screen'>
            {/* <Sider collapsible collapsed={collapsed} onCollapse={(value) => setCollapsed(value)}> */}
            <Sider>
              <div className="flex flex-col h-full">
                <div className="demo-logo-vertical" dir="rtl" />
                {/* Profile Section */}
                <div className="p-6 text-center cursor-pointer" onClick={() => setShowBranchPickerModal(true)}>
                  {/* <Badge
                    dot
                    color="green"
                    offset={[-10, 80]}
                    className="w-3 h-3 text-center"
                  >
                    <Avatar
                      size={80}
                      src="https://randomuser.me/api/portraits/women/44.jpg"
                      className="border-4 border-white shadow-md"
                    />
                  </Badge> */}
                  <Avatar
                    size={80}
                    className="border-4 border-grey shadow-md text-3xl font-medium"
                    style={{
                      backgroundColor: getAvatarColor(user.firstName),
                      color: '#fff'
                    }}
                  >
                    {getInitials(user.firstName)}
                  </Avatar>
                  <h3 className="mt-4 text-lg font-semibold text-gray-200 select-none">{user.firstName} {user.lastName}</h3>
                  {/* <p className="text-sm text-gray-300 select-none">{destinationResolver.displayName}</p> */}
                  <p className="text-sm text-gray-300 select-none">تست آبان</p>
                </div>
                {/* Date Time */}
                <div className="pb-6 text-center">
                  <p className="text-sm text-gray-400 select-none">{getDayName()}</p>
                  <p className="text-sm text-gray-400 select-none">{getFormatDateWithDay(language)}</p>
                </div>
                {
                  !!currentSelectedItem?.length &&
                  <Menu theme="dark" className="flex-grow" defaultSelectedKeys={currentSelectedItem} mode="inline" onSelect={onMenuItemSelect} items={MenuItems.map((item: any) => {
                    return {
                      ...item,
                      dir:`${isRTL ? "rtl" : "ltr"}`,
                      icon: <icon className="icon-lr">{item.icon}</icon>,
                      className: "text-start",
                      label: t(item.label)
                    }
                  })} />
                }
                <Menu theme="dark" mode="inline" items={ProfileMenuItems.map((item: any) => {
                  return {
                    ...item,
                    className: "text-start"
                  }
                })} />
              </div>
            </Sider>
            <Layout>
              <Content className='mx-4 my-2'>
                {children}
                <Outlet />
              </Content>
            </Layout>
          </Layout>
          :
          <>
            <div className="p-4">
              {children}
              <Outlet />
            </div>
          </>
        }
      </main>

      {/* Sticky Footer Menu */}
      <PortalMobileFooterMenu items={MenuItems} currentLink={currentLink} className="flex-none h-16 md:hidden" />
      {
        !!showBranchPickerModal &&
        <BranchPickerModal
          open={showBranchPickerModal}
          closeOnSave={true}
          onCancel={() => setShowBranchPickerModal(false)}
          onSave={onSelectBranchModalSave} />
      }
    </div>
  );
}
